// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ClipboardCheck, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';
import {
  getStockPurchases,
  postPhysicalInventoryCount
} from '../utils/stockUtils';
import { subscribeIngredients, getIngredientLabel } from '../utils/ingredientsUtils';
import Button from './Button';
import Toast from './ToastLegacy';
import { useModal } from './Modal';

// Note is required above this variance (kg or currency) — light fraud-control friction on top
// of the always-mandatory reason. Kept as a simple constant for now rather than a per-bakery
// setting, to keep the first version scoped.
const NOTE_REQUIRED_VARIANCE_KG = 2;
const NOTE_REQUIRED_VARIANCE_VALUE = 20;

const ADJUSTMENT_REASONS = ['Shrinkage', 'MeasurementError', 'ReceivingError', 'SpoilageNotLogged', 'Other'];
const REASON_LABEL_KEY = {
  Shrinkage: 'adjustmentReasonShrinkage',
  MeasurementError: 'adjustmentReasonMeasurementError',
  ReceivingError: 'adjustmentReasonReceivingError',
  SpoilageNotLogged: 'adjustmentReasonSpoilageNotLogged',
  Other: 'adjustmentReasonOther'
};

export default function PhysicalInventory({ currentUser, bakeryId }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { bakerySubdomain } = useParams();
  const { openModal, showModal } = useModal();

  const [step, setStep] = useState('capture'); // 'capture' | 'review'
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [activeLots, setActiveLots] = useState([]);
  const [ingredientsByKey, setIngredientsByKey] = useState(new Map());
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [countedByLot, setCountedByLot] = useState({}); // purchaseId -> kg string
  const [touchedLots, setTouchedLots] = useState(() => new Set()); // purchaseId set — only lots the baker actually edited
  const [reasonByLot, setReasonByLot] = useState({}); // purchaseId -> reason enum
  const [noteByLot, setNoteByLot] = useState({}); // purchaseId -> note string
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });
  const [showPostConfirm, setShowPostConfirm] = useState(false);

  const effectiveBakerId = bakeryId || currentUser?.uid;

  const getIngredientName = useCallback((keyOrName) => getIngredientLabel(keyOrName, ingredientsByKey, language), [ingredientsByKey, language]);

  const loadLots = useCallback(async () => {
    if (!effectiveBakerId) return;
    setLoading(true);
    try {
      const purchases = await getStockPurchases(effectiveBakerId);
      const active = purchases
        .filter(p => p.remainingQuantity > 0)
        .sort((a, b) => getIngredientName(a.ingredientKey || a.ingredientName).localeCompare(getIngredientName(b.ingredientKey || b.ingredientName), language));
      setActiveLots(active);

      const prefill = {};
      active.forEach(lot => {
        prefill[lot.id] = (lot.remainingQuantity / 1000).toFixed(2);
      });
      setCountedByLot(prefill);
      setTouchedLots(new Set());
      setReasonByLot({});
      setNoteByLot({});
      setStep('capture');
    } finally {
      setLoading(false);
    }
  }, [effectiveBakerId, getIngredientName, language]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = subscribeIngredients(effectiveBakerId, (byKey) => {
      setIngredientsByKey(byKey);
    });
    (async () => {
      try {
        const docRef = doc(db, 'bakeries', effectiveBakerId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setCurrencySymbol(docSnap.data().currencySymbol || '$');
      } catch (error) {
        console.error('Error loading baker settings:', error);
      }
    })();
    return () => unsubscribe && unsubscribe();
  }, [currentUser, effectiveBakerId]);

  useEffect(() => {
    loadLots();
  }, [loadLots]);

  const varianceForLot = (lot) => {
    if (!touchedLots.has(lot.id)) return 0; // untouched — never derive a "variance" from re-parsing the display prefill
    const countedKg = parseFloat(countedByLot[lot.id]);
    if (isNaN(countedKg)) return 0;
    return Math.round(countedKg * 1000) - lot.remainingQuantity;
  };

  const varianceLots = activeLots.filter(lot => varianceForLot(lot) !== 0);

  const noteRequiredForVarianceGrams = (varianceGrams, unitCost) => {
    const varianceKg = Math.abs(varianceGrams) / 1000;
    const varianceValue = Math.abs(varianceGrams * unitCost);
    return varianceKg > NOTE_REQUIRED_VARIANCE_KG || varianceValue > NOTE_REQUIRED_VARIANCE_VALUE;
  };

  const canProceedToReview = varianceLots.length > 0;

  const reviewIsComplete = () => {
    for (const lot of varianceLots) {
      if (!reasonByLot[lot.id]) return false;
      const varianceGrams = varianceForLot(lot);
      if (noteRequiredForVarianceGrams(varianceGrams, lot.unitCost) && !(noteByLot[lot.id] || '').trim()) return false;
    }
    return true;
  };

  const handlePost = () => setShowPostConfirm(true);

  const submitPost = async () => {
    setPosting(true);
    try {
      const lines = [];
      for (const lot of varianceLots) {
        lines.push({
          purchaseId: lot.id,
          ingredientKey: lot.ingredientKey || lot.ingredientName,
          ingredientLabelSnapshot: getIngredientName(lot.ingredientKey || lot.ingredientName),
          lotBatchNumber: lot.lotBatchNumber || '',
          expectedQtyAtCapture: lot.remainingQuantity,
          countedQty: Math.round(parseFloat(countedByLot[lot.id]) * 1000),
          reason: reasonByLot[lot.id],
          note: noteByLot[lot.id] || ''
        });
      }
      await postPhysicalInventoryCount(effectiveBakerId, currentUser.uid, lines);

      setToast({ show: true, type: 'success', message: t('countPosted', language) });
      setTimeout(() => {
        navigate(`/${bakerySubdomain || bakeryId || currentUser?.uid}/stock`);
      }, 1500);
    } catch (error) {
      if (error.message === 'DRIFT_DETECTED') {
        openModal({
          type: 'warning',
          title: t('refreshCount', language),
          message: t('driftDetected', language)
        });
        await loadLots();
      } else {
        openModal({
          type: 'error',
          title: language === 'fr' ? 'Erreur' : 'Error',
          message: error.message
        });
      }
    } finally {
      setPosting(false);
    }
  };

  if (!currentUser || loading) {
    return (
      <div className="text-center py-12">
        <div className="text-xl text-[var(--text-secondary)]">{t('loading', language) || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div>
      <Toast show={toast.show} type={toast.type} message={toast.message} />
      {showModal({ language })}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <ClipboardCheck size={32} />
          {t('physicalInventory', language)}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1"
        >
          <ChevronLeft size={16} />
          {t('backToHome', language) || (language === 'fr' ? 'Retour' : 'Back')}
        </Button>
      </div>

      {step === 'capture' && (
        <>
          <div className="bg-[var(--surface-card)] rounded-lg shadow-md p-6 mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t('ingredient', language)}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t('lotBatchNumber', language)}</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('systemQuantity', language)} (kg)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('actualQuantity', language)} (kg)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {activeLots.map(lot => {
                    const hasVariance = varianceForLot(lot) !== 0;
                    return (
                      <tr key={lot.id} className={hasVariance ? 'bg-[var(--status-warning-soft)]' : ''}>
                        <td className="px-3 py-2 text-sm font-medium">{getIngredientName(lot.ingredientKey || lot.ingredientName)}</td>
                        <td className="px-3 py-2 text-sm">{lot.lotBatchNumber || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right">{(lot.remainingQuantity / 1000).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={countedByLot[lot.id] ?? ''}
                            onChange={(e) => {
                              setCountedByLot({ ...countedByLot, [lot.id]: e.target.value });
                              setTouchedLots(prev => new Set(prev).add(lot.id));
                            }}
                            className="w-28 border border-[var(--border-strong)] rounded px-2 py-1 text-sm text-right"
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {activeLots.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-[var(--text-secondary)]">{t('noStock', language)}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={!canProceedToReview}
              onClick={() => setStep('review')}
              className="px-6 py-3 rounded-full font-semibold"
            >
              {t('reviewCount', language)}
            </Button>
          </div>
        </>
      )}

      {step === 'review' && (
        <>
          <div className="bg-[var(--surface-card)] rounded-lg shadow-md p-6 mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t('ingredient', language)}</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('expectedQty', language)} (kg)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('countedQty', language)} (kg)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('variance', language)} (kg)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold">{t('varianceValue', language)} ({currencySymbol})</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t('adjustmentReason', language)}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t('note', language)}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {activeLots.map(lot => {
                    const varianceGrams = varianceForLot(lot);
                    if (varianceGrams === 0) {
                      return (
                        <tr key={lot.id} className="text-[var(--text-muted)]">
                          <td className="px-3 py-2 text-sm">{getIngredientName(lot.ingredientKey || lot.ingredientName)}</td>
                          <td className="px-3 py-2 text-sm text-right">{(lot.remainingQuantity / 1000).toFixed(2)}</td>
                          <td className="px-3 py-2 text-sm text-right">{countedByLot[lot.id]}</td>
                          <td className="px-3 py-2 text-sm text-right">0.00</td>
                          <td className="px-3 py-2 text-sm text-right">-</td>
                          <td className="px-3 py-2 text-sm" colSpan={2}>-</td>
                        </tr>
                      );
                    }
                    const varianceValue = varianceGrams * lot.unitCost;
                    const noteRequired = noteRequiredForVarianceGrams(varianceGrams, lot.unitCost);
                    return (
                      <tr key={lot.id} className="bg-[var(--status-warning-soft)]">
                        <td className="px-3 py-2 text-sm font-medium">{getIngredientName(lot.ingredientKey || lot.ingredientName)}</td>
                        <td className="px-3 py-2 text-sm text-right">{(lot.remainingQuantity / 1000).toFixed(2)}</td>
                        <td className="px-3 py-2 text-sm text-right">{countedByLot[lot.id]}</td>
                        <td className={`px-3 py-2 text-sm text-right font-semibold ${varianceGrams > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {varianceGrams > 0 ? '+' : ''}{(varianceGrams / 1000).toFixed(2)}
                        </td>
                        <td className={`px-3 py-2 text-sm text-right font-semibold ${varianceValue > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {varianceValue > 0 ? '+' : ''}{varianceValue.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={reasonByLot[lot.id] || ''}
                            onChange={(e) => setReasonByLot({ ...reasonByLot, [lot.id]: e.target.value })}
                            className="border border-[var(--border-strong)] rounded px-2 py-1 text-xs w-full"
                          >
                            <option value="">{t('adjustmentReason', language)}</option>
                            {ADJUSTMENT_REASONS.map(r => (
                              <option key={r} value={r}>{t(REASON_LABEL_KEY[r], language)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={noteByLot[lot.id] || ''}
                            onChange={(e) => setNoteByLot({ ...noteByLot, [lot.id]: e.target.value })}
                            placeholder={noteRequired ? `${t('note', language)} *` : t('note', language)}
                            className={`border rounded px-2 py-1 text-xs w-full ${noteRequired && !(noteByLot[lot.id] || '').trim() ? 'border-red-500' : 'border-[var(--border-strong)]'}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep('capture')} className="flex items-center gap-1">
              <ChevronLeft size={16} /> {t('startCount', language)}
            </Button>
            <Button
              variant="primary"
              disabled={!reviewIsComplete() || posting}
              onClick={handlePost}
              className="px-6 py-3 rounded-full font-semibold"
            >
              {posting ? (t('loading', language) || '...') : t('postCount', language)}
            </Button>
          </div>
        </>
      )}

      {showPostConfirm && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[60] animate-slide-up">
          <div className="bg-[var(--surface-card)] rounded-lg shadow-2xl p-6 w-full max-w-md mx-4 border-2 border-[var(--border-subtle)]">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center">
                <ClipboardCheck size={24} className="text-[var(--action-primary)]" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2">
                  {t('postCount', language)}
                </h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  {varianceLots.length} {t('adjustedLines', language).toLowerCase()}.{' '}
                  {language === 'fr' ? 'Cette action est définitive.' : 'This cannot be edited afterward.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setShowPostConfirm(false)}
                variant="ghost"
                className="px-6 py-2 rounded-full"
              >
                {t('cancel', language)}
              </Button>
              <Button
                onClick={() => {
                  setShowPostConfirm(false);
                  submitPost();
                }}
                variant="primary"
                className="px-6 py-2 rounded-full"
              >
                {t('postCount', language)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
